import axios from 'axios';

// In production, use the current domain; in development, use env variable
const getApiUrl = () => {
  // If we're on a custom domain (not localhost or preview), use that domain
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    const currentOrigin = window.location.origin;
    // If on production custom domain, use it
    if (currentOrigin.includes('xperience-guides.com')) {
      return currentOrigin + '/api';
    }
  }
  // Otherwise use environment variable
  return (process.env.REACT_APP_BACKEND_URL || window.location.origin) + '/api';
};

const API_URL = getApiUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('vgt_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Experiences API
export const experiencesApi = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/experiences`, { params });
    return response.data;
  },
  
  getFeatured: async () => {
    const response = await axios.get(`${API_URL}/experiences/featured`);
    return response.data;
  },

  getFeaturedEvents: async () => {
    const response = await axios.get(`${API_URL}/events/featured`);
    return response.data;
  },

  promoteEvent: async (eventId, level = 'essential') => {
    const response = await axios.post(`${API_URL}/events/${eventId}/promote?level=${level}`, {}, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },

  getLocalEvents: async (params = {}) => {
    const response = await axios.get(`${API_URL}/events/local`, { params });
    return response.data;
  },

  linkEventToGuide: async (eventId) => {
    const response = await axios.post(`${API_URL}/guides/link-event/${eventId}`, {}, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },

  unlinkEventFromGuide: async (eventId) => {
    const response = await axios.delete(`${API_URL}/guides/link-event/${eventId}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },

  getGuideLinkedEvents: async (guideId) => {
    const response = await axios.get(`${API_URL}/guides/${guideId}/linked-events`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/experiences/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/experiences`, data, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await axios.put(`${API_URL}/experiences/${id}`, data, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/experiences/${id}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Bookings API
export const bookingsApi = {
  getAll: async () => {
    const response = await axios.get(`${API_URL}/bookings`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/bookings/${id}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/bookings`, data, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  updateStatus: async (id, status) => {
    const response = await axios.put(
      `${API_URL}/bookings/${id}/status`,
      { status },
      {
        headers: getAuthHeaders(),
        withCredentials: true
      }
    );
    return response.data;
  },
  
  getContactDetails: async (bookingId) => {
    const response = await axios.get(`${API_URL}/bookings/${bookingId}/contact-details`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Payments API
export const paymentsApi = {
  createCheckout: async (bookingId) => {
    const response = await axios.post(
      `${API_URL}/payments/checkout`,
      {
        booking_id: bookingId,
        origin_url: window.location.origin
      },
      {
        headers: getAuthHeaders(),
        withCredentials: true
      }
    );
    return response.data;
  },
  
  getStatus: async (sessionId) => {
    const response = await axios.get(`${API_URL}/payments/status/${sessionId}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Reviews API
export const reviewsApi = {
  create: async (data) => {
    const response = await axios.post(`${API_URL}/reviews`, data, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  getByExperience: async (experienceId) => {
    const response = await axios.get(`${API_URL}/reviews/experience/${experienceId}`);
    return response.data;
  }
};

// Explorers API
export const explorersApi = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/explorers`, { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/explorers/${id}`);
    return response.data;
  }
};

// Categories API
export const categoriesApi = {
  getAll: async () => {
    const response = await axios.get(`${API_URL}/categories`);
    return response.data;
  }
};

// Stats API
export const statsApi = {
  getExplorerStats: async () => {
    const response = await axios.get(`${API_URL}/stats/explorer`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  getExperimenterStats: async () => {
    const response = await axios.get(`${API_URL}/stats/experimenter`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Analytics API
export const analyticsApi = {
  getExplorerAnalytics: async (period = '30d') => {
    const response = await axios.get(`${API_URL}/explorer/analytics`, {
      params: { period },
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  trackPageView: async (data) => {
    try {
      await axios.post(`${API_URL}/analytics/track`, data);
    } catch (e) {
      // Silent fail - don't block user experience
    }
  },
  
  getOverview: async (days = 7) => {
    const response = await axios.get(`${API_URL}/admin/analytics/overview?days=${days}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  getTrafficDetails: async (days = 30) => {
    const response = await axios.get(`${API_URL}/admin/analytics/traffic?days=${days}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Favorites/Wishlist API
export const favoritesApi = {
  getAll: async () => {
    const response = await axios.get(`${API_URL}/favorites`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  add: async (experienceId) => {
    const response = await axios.post(`${API_URL}/favorites/${experienceId}`, {}, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  remove: async (experienceId) => {
    const response = await axios.delete(`${API_URL}/favorites/${experienceId}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  check: async (experienceId) => {
    const response = await axios.get(`${API_URL}/favorites/check/${experienceId}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Map & Availability API
export const mapApi = {
  getAvailableExplorers: async (params = {}) => {
    const response = await axios.get(`${API_URL}/map/explorers`, { params });
    return response.data;
  },
  
  getAllExplorers: async () => {
    const response = await axios.get(`${API_URL}/map/all-explorers`);
    return response.data;
  },
  
  updateAvailability: async (data) => {
    const response = await axios.put(`${API_URL}/explorer/availability`, data, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Upload API
export const uploadApi = {
  uploadProfilePhoto: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/upload/profile-photo`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    });
    return response.data;
  },
  
  deleteProfilePhoto: async (photoUrl) => {
    const response = await axios.delete(`${API_URL}/upload/profile-photo`, {
      params: { photo_url: photoUrl },
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  uploadExperiencePhoto: async (file, experienceId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (experienceId) {
      formData.append('experience_id', experienceId);
    }
    const response = await axios.post(`${API_URL}/upload/experience-photo`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    });
    return response.data;
  },
  
  uploadServicePhoto: async (file, serviceId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('service_id', serviceId);
    const response = await axios.post(`${API_URL}/upload/service-photo`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    });
    return response.data;
  }
};

// Explorer Services API
export const servicesApi = {
  getMyServices: async () => {
    const response = await axios.get(`${API_URL}/explorer/services`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  createService: async (data) => {
    const response = await axios.post(`${API_URL}/explorer/services`, data, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  updateService: async (serviceId, data) => {
    const response = await axios.put(`${API_URL}/explorer/services/${serviceId}`, data, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  deleteService: async (serviceId) => {
    const response = await axios.delete(`${API_URL}/explorer/services/${serviceId}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  getExplorerServices: async (userId) => {
    const response = await axios.get(`${API_URL}/explorers/${userId}/services`);
    return response.data;
  }
};

// Stripe Connect API
export const stripeConnectApi = {
  createAccount: async () => {
    const response = await axios.post(`${API_URL}/stripe/connect/account`, {}, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  createAccountLink: async () => {
    const response = await axios.post(
      `${API_URL}/stripe/connect/account-link`,
      { origin_url: window.location.origin },
      {
        headers: getAuthHeaders(),
        withCredentials: true
      }
    );
    return response.data;
  },
  
  getStatus: async () => {
    const response = await axios.get(`${API_URL}/stripe/connect/status`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Extended Bookings API
export const bookingsApiExtended = {
  ...bookingsApi,
  
  startExperience: async (bookingId) => {
    const response = await axios.post(
      `${API_URL}/bookings/${bookingId}/start`,
      {},
      {
        headers: getAuthHeaders(),
        withCredentials: true
      }
    );
    return response.data;
  },
  
  completeExperience: async (bookingId) => {
    const response = await axios.post(
      `${API_URL}/bookings/${bookingId}/complete`,
      {},
      {
        headers: getAuthHeaders(),
        withCredentials: true
      }
    );
    return response.data;
  },
  
  openDispute: async (bookingId, reason) => {
    const response = await axios.post(
      `${API_URL}/bookings/${bookingId}/dispute`,
      { booking_id: bookingId, reason },
      {
        headers: getAuthHeaders(),
        withCredentials: true
      }
    );
    return response.data;
  }
};

// Earnings API
export const earningsApi = {
  getExplorerEarnings: async () => {
    const response = await axios.get(`${API_URL}/explorer/earnings`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};

// Messaging API
export const messagesApi = {
  send: async (bookingId, content) => {
    const response = await axios.post(
      `${API_URL}/messages`,
      { booking_id: bookingId, content },
      {
        headers: getAuthHeaders(),
        withCredentials: true
      }
    );
    return response.data;
  },
  
  getByBooking: async (bookingId) => {
    const response = await axios.get(`${API_URL}/messages/${bookingId}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  getUnreadCount: async () => {
    const response = await axios.get(`${API_URL}/messages/unread/count`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  },
  
  getConversations: async () => {
    const response = await axios.get(`${API_URL}/conversations`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  }
};
